import React, { useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { QK } from "../../../base/qk";
import { getAllergiesByID, deleteAllergyByID } from "../../../api/services/allergy";
import DataTable from "../../Table/DataTable";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import AllergiesModal from "./AllergiesModal";

const Allergies = () => {
  const queryClient = useQueryClient();
  const [showAllergiesModal, setShowAllergiesModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const { id: clientID } = useParams();

  const { data: allergies, isLoading } = useQuery(
    [QK.allergies, clientID],
    () => getAllergiesByID(clientID),
    {
      enabled: !!clientID,
    }
  );

  const deleteMutation = useMutation(deleteAllergyByID, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.allergies, clientID]);
      enqueueSnackbar("Allergy deleted successfully", { variant: "success" });
      setShowConfirmModal(false);
    },
    onError: () => {
      enqueueSnackbar("Failed to delete allergy", { variant: "error" });
    },
  });

  const handleAddClick = () => setShowAllergiesModal(true);
  const handleAllergiesClose = () => setShowAllergiesModal(false);

  const handleUpdate = (id) => {
    enqueueSnackbar("Edit functionality coming soon", { variant: "info" });
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  };

  const handleCloseConfirm = () => setShowConfirmModal(false);

  const headers = ["name", "comment"];

  return (
    <>
      <Card
        sx={{
          borderRadius: "16px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <CardHeader
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: "1px solid #F2F4F7",
          }}
          title={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    bgcolor: "#EFF8FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MedicalServicesIcon sx={{ color: "#2469F0", fontSize: 22 }} />
                </Box>
                <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                  Allergies
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: "#F2F4F7",
                    px: 1,
                    py: 0.5,
                    borderRadius: "20px",
                    color: "#667085",
                    fontWeight: 500,
                  }}
                >
                  {allergies?.length || 0}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddClick}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  bgcolor: "#2469F0",
                  "&:hover": {
                    bgcolor: "#1A5BD4",
                    boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
                  },
                }}
              >
                Add Allergy
              </Button>
            </Box>
          }
        />

        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography color="text.secondary">Loading allergies...</Typography>
            </Box>
          ) : allergies?.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                No allergies recorded
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={handleAddClick}
                sx={{ textTransform: "none", color: "#2469F0" }}
              >
                Add your first allergy
              </Button>
            </Box>
          ) : (
            <DataTable
              data={allergies}
              headers={headers}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              canUpdate={true}
              canDelete={true}
            />
          )}
        </CardContent>
      </Card>

      <AllergiesModal
        show={showAllergiesModal}
        onHide={handleAllergiesClose}
      />

      <ConfirmModal
        show={showConfirmModal}
        handleClose={handleCloseConfirm}
        handleConfirm={handleConfirmDelete}
        title="Delete Allergy"
        body="Are you sure you want to delete this allergy? This action cannot be undone."
        isDelete={true}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default Allergies;